// @flow

import * as React from 'react';
import classnames from '@ta/common.classnames';
import { Link } from '@ta/common.routing';
import { BubbleRating } from '@ta/styleguide.bubble-rating';
import { MemberEventOnObjectBlock } from '@ta/social.member-event';
import { InteractionLogger } from '@ta/tracking.interactions';
import EventDate from '@ta/social.review-info';
import { CardSection } from '@ta/social.section-core';

import ManagedFeedSectionPhotoCarousel from './ManagedFeedSectionPhotoCarousel';
import ReasonForCrust from './ReasonForCrust';
import styles from './ReviewSection.less';

type ReviewSectionLayout = 'FULL' | 'REPOST_NO_MODAL' | 'REPOST_WITH_MODAL';

export type ReviewSectionProps = {
  member: any,
  actor: any,
  actorForUrl?: any,
  review: any,
  section?: ?any,
  deleteObject: ?() => void,
  isSingleObject: boolean,
  layout: ReviewSectionLayout,
  clusterId?: string,
  socialStatisticsBar?: React.Node,
  socialActionBar?: React.Node,
};

const ReviewSection = ({
  review,
  isSingleObject,
  layout,
  member,
  actor,
  actorForUrl,
  section,
  deleteObject,
  clusterId,
  socialStatisticsBar,
  socialActionBar,
}: ReviewSectionProps) => {
  if (!review.object) {
    return null;
  }

  const MAX = 160;

  /** ML-2713 turn this off for now */
  const turnOnReasonFor = false;

  const header = (
    <React.Fragment>
      {turnOnReasonFor && <ReasonForCrust section={section} />}
      <MemberEventOnObjectBlock
        objectEventType="onReview"
        object={review.object}
        member={member}
        actor={actor}
        deleteObject={deleteObject}
        isEmbedded={layout === 'REPOST_WITH_MODAL' || layout === 'REPOST_NO_MODAL'}
      />
    </React.Fragment>
  );

  const carouselMetadata = {
    id: review.object.id,
    caption: review.object.text,
    socialReferences: review.object.socialReferences,
    socialStatistics: review.object.socialStatistics,
    orderedLocations: [review.object.location],
    createdDate: review.object.publishedDate,
    __typename: review.object.__typename,
    numPhotos: review?.photos ? review.object.photos.length : 0,
    route: review.object.route,
  };

  const reviewContent = (showFullText, onClick) => {
    const reviewText = showFullText ? (
      review.object.text
    ) : (
      <React.Fragment>
        {review.object.text.substring(0, MAX)}
        {review.object.text.length > MAX && '...'}
      </React.Fragment>
    );

    const href = review.object.route?.url;

    const reviewBlock = (
      <div className={href ? classnames(styles.review, styles.linked) : styles.review}>
        <BubbleRating rating={review.object.rating} />
        <div className={href ? classnames(styles.title, styles.linked) : styles.title}>
          {review.object.title}
        </div>
        <div className={classnames(styles.body_text, { [styles.full_text]: showFullText })}>
          <q className={styles.quote}>{reviewText}</q>
        </div>
        <EventDate
          stayDate={review.object.tripInfo && review.object.tripInfo.stayDate}
          placeType={review.object.location && review.object.location.placeType}
        />
      </div>
    );

    return href ? (
      <Link href={href} onClick={onClick}>
        {reviewBlock}
      </Link>
    ) : (
      reviewBlock
    );
  };

  const content = [];
  // Include a photo carousel if we have one
  if (review?.object?.photos?.length > 0) {
    content.push(
      <CardSection.Content key={review.object.id} fullWidth>
        <InteractionLogger>
          {logFeedClick => (
            <ManagedFeedSectionPhotoCarousel
              metadata={carouselMetadata}
              deleteObject={deleteObject}
              member={member}
              photos={review?.object?.photos}
              onCarouselClick={e => {
                logFeedClick(e, 'feedClick');
              }}
              actor={actor}
              actorForUrl={actorForUrl}
              additionalModalContent={reviewContent(true)}
              socialStatisticsBar={socialStatisticsBar}
              socialActionBar={socialActionBar}
              includeRoutedModal={layout === 'FULL' || layout === 'REPOST_WITH_MODAL'}
              overflowMenuObject={review.object}
            />
          )}
        </InteractionLogger>
      </CardSection.Content>,
    );
  }
  content.push(
    <CardSection.Content key={`review_${review.object.id}`}>
      <div className={classnames(styles.review_wrap)}>
        <InteractionLogger>
          {logFeedClick => reviewContent(false, e => logFeedClick(e, 'feedClick'))}
        </InteractionLogger>
      </div>
    </CardSection.Content>,
  );

  return (
    <CardSection
      isSingleObject={isSingleObject}
      isEmbedded={layout === 'REPOST_WITH_MODAL' || layout === 'REPOST_NO_MODAL'}
      header={header}
      content={content}
      socialActionBar={socialActionBar}
      socialStatisticsBar={socialStatisticsBar}
      pois={review.object.location ? [review.object.location] : []}
      clusterId={clusterId}
      sponsorship={section?.sponsorship}
    />
  );
};

ReviewSection.defaultProps = {
  isSingleObject: false,
  isEmbedded: false,
  member: null,
  actor: null,
  actorForUrl: undefined,
  section: null,
  deleteObject: null,
  clusterId: undefined,
};

export default ReviewSection;
